/*
  Hazemor add-on (Theme settings → Hazemor brand → Add-on)

  1. Buy panel: when the add-on tile next to Add to cart is ticked, the product form's submit is taken
     over and both lines go to the cart in one request. Horizon's cart drawer, cart icon and line-item
     animations are driven by the same cart:lines-update event its own product form uses.
     If the add-on can't be added (e.g. it sold out a moment ago), the main product is still added the
     normal way and a short note explains what happened.
  2. Buy it now: Shopify's express buttons only ever buy the main product, so while the tile is ticked
     they step aside for the section's own Buy it now, which opens checkout with both products
     (a cart permalink: /cart/<variant>:<qty>,<add-on variant>:1 — the shopper's cart is left as it is).
  3. Cart drawer: the "Pairs with your sauna" card's Add button adds the add-on on its own.

  Loaded as a module after Horizon's import map (snippets/hz-brand.liquid).
*/

const eventsReady = import('@shopify/events').catch(() => null);

const routes = () => (window.Theme && window.Theme.routes) || {};
const cartAddUrl = () => routes().cart_add_url || '/cart/add';
const cartUrl = () => routes().cart_url || '/cart';

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function fetchCart() {
  const res = await fetch(`${cartUrl()}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Cart request failed: ${res.status}`);
  return res.json();
}

function quantityInCart(cart, variantId) {
  if (!cart || !Array.isArray(cart.items)) return 0;
  return cart.items
    .filter((item) => String(item.variant_id) === String(variantId))
    .reduce((sum, item) => sum + (item.quantity || 0), 0);
}

/**
 * Adds lines to the cart and lets the theme react exactly as it does to its own add to cart.
 * @param {HTMLElement} source - element the cart event is dispatched from
 * @param {{id: string, quantity: number}[]} lines
 * @param {{sourceId?: string, productId?: string, context?: string}} meta
 */
async function addLines(source, lines, meta = {}) {
  const events = await eventsReady;
  const LinesEvent = events && events.CartLinesUpdateEvent;
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  let deferred = null;

  if (LinesEvent) {
    deferred = LinesEvent.createPromise();
    source.dispatchEvent(
      new LinesEvent({
        action: 'add',
        context: meta.context || 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );
  }

  let response;
  try {
    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity })),
        sections: cartSectionIds().join(','),
      }),
    });
    response = await res.json();
  } catch (error) {
    if (deferred) deferred.reject(error);
    throw error;
  }

  const ok = !response.status;
  const cart = await fetchCart().catch(() => null);

  if (deferred) {
    if (cart) {
      deferred.resolve({
        cart: LinesEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'hz-addon',
          sourceId: meta.sourceId || 'hz-addon',
          itemCount,
          productId: meta.productId,
          sections: ok ? response.sections : undefined,
          didError: !ok,
        },
      });
    } else {
      deferred.reject(new Error('Cart refresh failed'));
    }
  } else if (ok) {
    // Theme events unavailable: show the result on the cart page instead.
    window.location.href = cartUrl();
  }

  if (!ok) {
    const error = new Error(response.description || response.message || 'Add to cart failed');
    error.cart = cart;
    throw error;
  }
  return cart;
}

function showMessage(scope, text) {
  const msg = scope && scope.querySelector('[data-hz-addon-msg]');
  if (!msg) return;
  msg.textContent = text;
  msg.hidden = false;
  clearTimeout(msg._hzTimer);
  msg._hzTimer = setTimeout(() => {
    msg.hidden = true;
    msg.textContent = '';
  }, 9000);
}

function syncTile(scope) {
  if (!scope) return;
  const input = scope.querySelector('[data-hz-addon-input]');
  const on = Boolean(input && input.checked);
  scope.classList.toggle('is-addon-on', on);
  // Our Buy it now stands in only where the express buttons would otherwise be showing.
  const buyNow = scope.querySelector('[data-hz-addon-buynow]');
  if (buyNow) {
    const express = scope.querySelector('.accelerated-checkout-block');
    buyNow.hidden = !(on && express && !express.hidden);
  }
  // The sticky bar (rendered beside the section) adds the same way, so it says so too.
  const barNote = scope.parentElement && scope.parentElement.querySelector('[data-hz-bar-addon]');
  if (barNote) barNote.hidden = !on;
}

function syncAll() {
  document.querySelectorAll('.hz-buy').forEach(syncTile);
}

function productForm(scope) {
  const component = scope && scope.querySelector('product-form-component');
  const form = component && (component.querySelector('form[action*="/cart/add"]') || component.querySelector('form'));
  return { component, form };
}

function formQuantity(form, component) {
  const field = form.elements.namedItem('quantity');
  const value = field && 'value' in field ? parseInt(field.value, 10) : NaN;
  return Math.max(1, value || Number(component && component.dataset.quantityDefault) || 1);
}

/* ------------------------------------------------------------------ buy panel */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.matches('[data-hz-addon-input]')) {
    syncTile(input.closest('.hz-buy'));
  }
});

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.hzAddonBypass) {
      delete form.dataset.hzAddonBypass;
      return;
    }

    const component = form.closest('product-form-component');
    const scope = form.closest('.hz-buy');
    const toggle = scope && scope.querySelector('[data-hz-addon-input]');
    if (!component || !toggle || !toggle.checked || !toggle.value) return;

    const variantId = form.querySelector('input[name="id"]')?.value;
    if (!variantId) return;

    // Leave disabled / sold-out states to the theme.
    const submitButtons = component.querySelectorAll('button[type="submit"], button[name="add"]');
    if (Array.from(submitButtons).some((button) => button.disabled)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (scope.dataset.hzAddonBusy) return;
    scope.dataset.hzAddonBusy = 'true';

    const quantityInput = form.elements.namedItem('quantity');
    const quantity = formQuantity(form, component);
    const inCartBefore = Number((quantityInput && quantityInput.dataset && quantityInput.dataset.cartQuantity) || 0);
    const submitter = event.submitter;

    addLines(
      component,
      [
        { id: variantId, quantity },
        { id: toggle.value, quantity: 1 },
      ],
      { sourceId: component.id || 'hz-addon', productId: component.dataset.productId }
    )
      .then(() => {
        toggle.checked = false;
        syncTile(scope);
      })
      .catch(async (error) => {
        // The add-on didn't go in. Make sure the main product still does.
        const cart = error && error.cart ? error.cart : await fetchCart().catch(() => null);
        if (quantityInCart(cart, variantId) <= inCartBefore) {
          form.dataset.hzAddonBypass = 'true';
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
          } else if (submitter) {
            submitter.click();
          }
        }
        toggle.checked = false;
        syncTile(scope);
        showMessage(scope, 'The add-on couldn’t be added just now — your sauna was added on its own.');
      })
      .finally(() => {
        delete scope.dataset.hzAddonBusy;
      });
  },
  true
);

/* ------------------------------------------------------------------ buy it now with the add-on */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-hz-addon-buynow]') : null;
  if (!button || button.getAttribute('aria-busy') === 'true') return;

  const scope = button.closest('.hz-buy');
  const toggle = scope && scope.querySelector('[data-hz-addon-input]');
  const { component, form } = productForm(scope);
  const variantId = form && form.querySelector('input[name="id"]')?.value;
  if (!toggle || !toggle.checked || !toggle.value || !variantId) return;

  // Same rule as the theme's own buttons: nothing happens while the product can't be bought.
  const submitButtons = component.querySelectorAll('button[type="submit"], button[name="add"]');
  if (Array.from(submitButtons).some((submit) => submit.disabled)) return;

  event.preventDefault();
  button.setAttribute('aria-busy', 'true');
  const quantity = formQuantity(form, component);
  window.location.assign(`/cart/${encodeURIComponent(variantId)}:${quantity},${encodeURIComponent(toggle.value)}:1`);
});

/* Back/forward cache and restored form state: keep the tile, express buttons and Buy it now in step. */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    document.querySelectorAll('[data-hz-addon-buynow][aria-busy]').forEach((button) => button.removeAttribute('aria-busy'));
  }
  syncAll();
});
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAll, { once: true });
} else {
  syncAll();
}

/* ------------------------------------------------------------------ cart drawer card */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-hz-addon-add]') : null;
  if (!button || button.disabled || !button.dataset.variantId) return;
  event.preventDefault();

  const card = button.closest('[data-hz-cart-addon]');
  const label = button.querySelector('.hz-cart-addon__label');
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (label) label.textContent = 'Adding';

  addLines(button, [{ id: button.dataset.variantId, quantity: 1 }], {
    sourceId: 'hz-cart-addon',
    productId: button.dataset.productId,
    context: 'cart',
  })
    .catch(() => {
      showMessage(card, 'Couldn’t add it just now — please try again.');
    })
    .finally(() => {
      // On success the drawer re-renders without this card; this only matters if it didn't.
      if (!button.isConnected) return;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label) label.textContent = 'Add';
    });
});
